import { Flex, useToast } from '@lego/klik-ui';
import { useCallback, useMemo } from 'react';
import { writeTableStateToUrl } from '@/components/common/table/store/tableUrlParams';
import type { AssignResult } from '@/components/device/AssignNetworkConfigPopover';
import { AssignNetworkConfigPopover } from '@/components/device/AssignNetworkConfigPopover';
import type { UnassignResult } from '@/components/device/UnassignNetworkConfigPopover';
import { UnassignNetworkConfigPopover } from '@/components/device/UnassignNetworkConfigPopover';
import {
  useAssignDeviceNetworkConfigs,
  useDeviceNetworkConfigs,
  useUnassignDeviceNetworkConfigs,
} from '@/hooks/device';
import { type NetworkConfigResponse, useWorkspaceNetworkConfigs } from '@/hooks/workspace';
import { paths } from '@/utility/global_paths';
import { ToastOptions } from '@/utility/ToastOptions';
import type { EnrichedDeviceNetworkConfig } from '../common/table/column_definitions/DeviceNetworkConfig';
import { deviceNetworkConfigColumns } from '../common/table/column_definitions/DeviceNetworkConfig';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { NetworkConfigContent } from '../network_config/NetworkConfigContent';

type Props = {
  deviceId: number;
  workspaceId: number;
  canWrite: boolean;
};

export const DeviceNetworkConfigs = ({ deviceId, workspaceId, canWrite }: Props) => {
  const toast = useToast();

  const { data, isLoading, isValidating, mutate } = useDeviceNetworkConfigs(workspaceId, deviceId);

  const {
    data: workspaceConfigsData,
    error: workspaceConfigsError,
    isLoading: workspaceConfigsLoading,
  } = useWorkspaceNetworkConfigs(workspaceId, { limit: '1000' });

  const assignConfigs = useAssignDeviceNetworkConfigs(workspaceId, deviceId, () => {
    console.error('Failed to assign network configs');
  });

  const unassignConfigs = useUnassignDeviceNetworkConfigs(workspaceId, deviceId, () => {
    console.error('Failed to unassign network configs');
  });

  const assignedConfigIds = useMemo(() => {
    const ids = new Set<number>();
    for (const config of data?.deviceNetworkConfigs ?? []) {
      ids.add(config.networkConfigId);
    }
    return ids;
  }, [data]);

  const availableConfigs = useMemo(
    () =>
      (workspaceConfigsData?.networkConfigs ?? []).filter(
        (c: NetworkConfigResponse) => !assignedConfigIds.has(c.id)
      ),
    [workspaceConfigsData, assignedConfigIds]
  );

  const enrichedData: EnrichedDeviceNetworkConfig[] = useMemo(() => {
    const configMap = new Map((workspaceConfigsData?.networkConfigs ?? []).map((c) => [c.id, c]));
    return (data?.deviceNetworkConfigs ?? []).map((assignment) => {
      const config = configMap.get(assignment.networkConfigId);
      return {
        ...assignment,
        label: config?.label,
        fileName: config?.fileName,
        connectionName: config?.connectionName,
        connectionType: config?.connectionType,
        interfaceName: config?.interfaceName,
      };
    });
  }, [data, workspaceConfigsData]);

  const handleAssign = useCallback(
    async (networkConfigIds: number[]): Promise<AssignResult> => {
      let result: Awaited<ReturnType<typeof assignConfigs>>;
      try {
        result = await assignConfigs({
          assignments: networkConfigIds.map((networkConfigId) => ({ deviceId, networkConfigId })),
        });
      } catch {
        return { success: false };
      }

      const succeededCount = result.succeeded.length;
      const failedCount = result.failed.length;

      if (failedCount === 0) {
        toast(
          ToastOptions.success(
            'Network configs assigned',
            `Successfully assigned ${succeededCount} network config${succeededCount !== 1 ? 's' : ''}.`,
            3000
          )
        );
        return { success: true };
      }

      if (succeededCount > 0) {
        toast(
          ToastOptions.error(
            'Partial assignment',
            `${succeededCount} assigned, ${failedCount} failed.`
          )
        );
      }

      return {
        success: false,
        failures: result.failed.map((f) => ({
          networkConfigId: f.networkConfigId,
          error: f.error,
        })),
      };
    },
    [assignConfigs, deviceId, toast]
  );

  const handleUnassign = useCallback(
    async (networkConfigId: number) => {
      let result: Awaited<ReturnType<typeof unassignConfigs>>;
      try {
        result = await unassignConfigs({
          assignments: [{ deviceId, networkConfigId }],
        });
      } catch {
        return;
      }

      if (result.failed.length > 0) {
        toast(ToastOptions.error('Unassign failed', result.failed.map((f) => f.error).join(', ')));
      } else {
        toast(
          ToastOptions.success(
            'Network config unassigned',
            'The network config has been unassigned from the device.',
            3000
          )
        );
      }
    },
    [unassignConfigs, deviceId, toast]
  );

  const handleBatchUnassign = useCallback(
    async (networkConfigIds: number[]): Promise<UnassignResult> => {
      let result: Awaited<ReturnType<typeof unassignConfigs>>;
      try {
        result = await unassignConfigs({
          assignments: networkConfigIds.map((networkConfigId) => ({ deviceId, networkConfigId })),
        });
      } catch {
        return { success: false };
      }

      const succeededCount = result.succeeded.length;
      const failedCount = result.failed.length;

      if (failedCount === 0) {
        toast(
          ToastOptions.success(
            'Network configs unassigned',
            `Successfully unassigned ${succeededCount} network config${succeededCount !== 1 ? 's' : ''}.`,
            3000
          )
        );
        return { success: true };
      }

      if (succeededCount > 0) {
        toast(
          ToastOptions.error(
            'Partial unassignment',
            `${succeededCount} unassigned, ${failedCount} failed.`
          )
        );
      }

      return {
        success: false,
        failures: result.failed.map((f) => ({
          networkConfigId: f.networkConfigId,
          error: f.error,
        })),
      };
    },
    [unassignConfigs, deviceId, toast]
  );

  const getManagePath = useCallback(
    (networkConfigId: number) => {
      const params = writeTableStateToUrl(
        new URLSearchParams(),
        `NetworkConfigs-${workspaceId}`,
        [],
        { pageIndex: 0, pageSize: 15 },
        [{ id: 'id', value: String(networkConfigId) }]
      );
      return `${paths.workspaceNetworkConfigs(workspaceId)}?${params.toString()}`;
    },
    [workspaceId]
  );

  const columns = useMemo(
    () => deviceNetworkConfigColumns(canWrite ? handleUnassign : undefined, getManagePath),
    [canWrite, getManagePath, handleUnassign]
  );

  return (
    <TableStoreProvider
      initialColumnVisibility={{ id: false, networkConfigId: false }}
      initialSorting={[{ desc: true, id: 'id' }]}
      name="DeviceNetworkConfigs"
    >
      <Table
        addButton={
          canWrite
            ? () => (
                <Flex>
                  <UnassignNetworkConfigPopover
                    assignedConfigs={enrichedData}
                    borderRightRadius="0px"
                    onUnassign={handleBatchUnassign}
                  />
                  <AssignNetworkConfigPopover
                    availableConfigs={availableConfigs}
                    borderRadius="0px"
                    hasError={Boolean(workspaceConfigsError)}
                    isLoading={workspaceConfigsLoading}
                    onAssign={handleAssign}
                  />
                </Flex>
              )
            : undefined
        }
        columns={columns}
        count={enrichedData.length}
        data={enrichedData}
        description="Network configs assigned to this device. Requires core version 15 or above to apply."
        expandableRow={({ original }) => (
          <NetworkConfigContent
            networkConfigId={original.networkConfigId}
            workspaceId={workspaceId}
          />
        )}
        isLoading={Boolean(isLoading || !data)}
        isValidating={isValidating}
        onRowClick={(_, row) => row.toggleExpanded()}
        refresh={mutate}
        title="Network Configs"
      />
    </TableStoreProvider>
  );
};
